/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type DependencyList,
  type ReactNode,
} from 'react'

export type BackHandler = () => void

export interface BackStackContextValue {
  push: (handler: BackHandler) => () => void
  pop: () => boolean
  size: () => number
}

const BackStackContext = createContext<BackStackContextValue | null>(null)

export const BackStackProvider = ({ children }: { children: ReactNode }) => {
  const stackRef = useRef<BackHandler[]>([])

  const push = useCallback((handler: BackHandler) => {
    stackRef.current.push(handler)
    return () => {
      // Remove by identity so out-of-order unmounts remove the exact handler
      const index = stackRef.current.lastIndexOf(handler)
      if (index !== -1) {
        stackRef.current.splice(index, 1)
      }
    }
  }, [])

  const pop = useCallback((): boolean => {
    const handler = stackRef.current.pop()
    if (!handler) return false
    try {
      handler()
    } catch (error) {
      console.error('[BackStackContext] Error executing back handler:', error)
    }
    return true
  }, [])

  const size = useCallback((): number => stackRef.current.length, [])

  const value = useMemo<BackStackContextValue>(() => ({
    push,
    pop,
    size,
  }), [push, pop, size])

  return (
    <BackStackContext.Provider value={value}>
      {children}
    </BackStackContext.Provider>
  )
}

export const useBackStack = (): BackStackContextValue => {
  const context = useContext(BackStackContext)
  if (!context) {
    return {
      push: () => () => {},
      pop: () => false,
      size: () => 0,
    }
  }
  return context
}

/**
 * Convenience hook for registering a back handler when an overlay is open/active.
 * Keeps latest handler in a ref so the registered identity is stable across renders.
 */
export function usePushBackHandler(
  handler: BackHandler,
  activeOrDeps: boolean | DependencyList = true,
  active = true,
) {
  const { push } = useBackStack()
  const handlerRef = useRef(handler)

  useEffect(() => {
    handlerRef.current = handler
  })

  const isEnabled = typeof activeOrDeps === 'boolean' ? activeOrDeps : active
  const deps = Array.isArray(activeOrDeps) ? activeOrDeps : []

  useEffect(() => {
    if (!isEnabled) return
    const stableHandler: BackHandler = () => {
      handlerRef.current()
    }
    return push(stableHandler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled, push, ...deps])
}
